import React, { useMemo, memo } from 'react'
import { PropTypes } from 'prop-types'
import LoadingParent from 'components/LoadingParent'
import {
  defaultProgramResolver,
  CourseManagerType,
} from 'layers/content/v2/Hocs/CourseManagerGetter'
import { programs } from 'layers/content/v2/hooks/useCourseManager'
import { useCurriculumCourseTrees } from 'layers/content/v3/hooks/useCurriculumCourseTrees'

/**
 * v3 replacement for `CourseManagerGetter` that fetches course trees from the
 * curriculum service POST /v1/audienced-course-versions endpoint.
 *
 * Drop-in compatible signature with `CourseManagerGetter`:
 *   CurriculumCourseManagerGetter(Component, options)
 *
 * Options:
 *   - programResolver        — same as legacy; defaults to URL-based resolver
 *   - overrideHsPreferences  — accepted for API parity (currently unused on
 *     this path; the public curriculum endpoint does not yet support
 *     preference overrides)
 *   - allowAuthoredCourseTree — opt this surface in to the full authored
 *     course structure for programs configured with `useAuthoredCourseTree`
 */
export function CurriculumCourseManagerGetter(Component, options = {}) {
  const {
    programResolver = defaultProgramResolver,
    overrideHsPreferences = {},
    allowAuthoredCourseTree = false,
  } = options

  const CurriculumCourseManagerGetterComponent = memo(
    function CurriculumCourseManagerGetterComponent(props) {
      const { match, currentSite } = props

      const program = useMemo(() => programResolver(match, props), [match?.url])

      const stableOverrideHsPreferences = useMemo(() => overrideHsPreferences, [
        JSON.stringify(overrideHsPreferences),
      ])

      const explicitSiteId = currentSite?.siteId ?? null

      const courseManager = useCurriculumCourseTrees({
        program: program === programs.none ? null : program,
        siteId: explicitSiteId,
        overrideHsPreferences: stableOverrideHsPreferences,
        allowAuthoredCourseTree,
      })

      if (!program) {
        throw new Error('Program not found')
      }

      if (program === programs.none) {
        return <Component {...props} courseManager={null} />
      }

      if (courseManager?.error) {
        console.warn(
          `[CurriculumCourseManagerGetter] Error loading course manager: ${courseManager.error.message}`,
          'Returning component without course manager data',
        )
        return (
          <Component
            {...props}
            courseManager={{ ...courseManager, loading: false }}
          />
        )
      }

      if (
        (!!courseManager?.loading || !!courseManager?.isUserContextLoading) &&
        !courseManager?.error
      ) {
        return <LoadingParent loadingText={'Loading user data...'} />
      }

      return <Component {...props} courseManager={courseManager} />
    },
  )

  CurriculumCourseManagerGetterComponent.propTypes = {
    currentSite: PropTypes.shape({
      siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    match: PropTypes.shape({
      url: PropTypes.string,
    }),
  }

  CurriculumCourseManagerGetterComponent.displayName = `CurriculumCourseManagerGetter(${Component.displayName ||
    Component.name ||
    'Component'})`

  return CurriculumCourseManagerGetterComponent
}

CurriculumCourseManagerGetter.displayName = 'CurriculumCourseManagerGetter'

export { CourseManagerType }
